// AHA-6796 recommendation confidence score (memo §10) display helpers. The
// score is server-computed (system-selector-api's compute-confidence-score
// engine); the GUI only fetches and renders it — never recompute
// client-side. Types are local working copies — canonical home pending
// @alpinehomeair/common republish (the ducted-coverage precedent).

export type ConfidenceScoreFieldStatusType =
  | 'required'
  | 'conditional'
  | 'recommended'
  | 'optional';

export type ConfidenceScoreFieldResultType = {
  id: string;
  label: string;
  side: 'have' | 'want';
  status: ConfidenceScoreFieldStatusType;
  points: number;
  earned: boolean;
};

// Gate config resolved with the score (AHA-6796 enforcement UX): one live
// fetch at gate time yields the fresh score AND the current admin
// mode/threshold. Optional — an older API deploy omits it and the gate
// fails open.
export type ConfidenceScoreHardGateType = {
  mode: 'block' | 'notify';
  threshold: number;
  gateFieldIds: string[];
};

export type ConfidenceScoreResultType = {
  overall: number;
  band: { key: string; label: string };
  dcs: number;
  pcs: number;
  hardGated: boolean;
  // Sorted by the server: highest-point unknown fields first.
  fields: ConfidenceScoreFieldResultType[];
  hardGate?: ConfidenceScoreHardGateType;
};

export type ConfidenceScoreOutcomeType =
  | { applicable: false }
  | ({ applicable: true } & ConfidenceScoreResultType);

// Admin config shape for the 'confidenceScore' Settings row.
export type ConfidenceScoreBandConfigType = {
  min: number;
  key: string;
  label: string;
};

export type ConfidenceScoreHaveFieldConfigType = {
  key: string;
  label: string;
  status: ConfidenceScoreFieldStatusType;
  defaultSafe?: boolean;
  predicate?: 'component_kept' | 'combustion_fuel';
};

export type ConfidenceScoreWantFieldConfigType = {
  code: string;
  label: string;
  status: ConfidenceScoreFieldStatusType;
  anyOf?: string[];
  predicate?: 'adding_cooling';
};

export type ConfidenceScoreConfigType = {
  haveWeight: number;
  wantWeight: number;
  points: {
    required: number;
    conditional: number;
    recommended: number;
    optional: number;
  };
  capValue: number;
  hardGate: { mode: 'block' | 'notify'; threshold: number };
  bands: ConfidenceScoreBandConfigType[];
  haveFields: Record<string, ConfidenceScoreHaveFieldConfigType[]>;
  hardGateFields: Record<string, string[]>;
  wantFields: ConfidenceScoreWantFieldConfigType[];
};

// The server sorts fields missing-first / highest-points-first, so the top
// missing entries are a prefix.
export function getTopMissingFields(
  result: ConfidenceScoreResultType,
  count: number
): ConfidenceScoreFieldResultType[] {
  return result.fields.filter((field) => !field.earned).slice(0, count);
}

// Gate-modal variant of the list above: missing fields with hard-gate
// fields first (the answers that lift the DCS cap), then the server's own
// point order, capped at `count`.
export function getGateMissingFields(
  result: ConfidenceScoreResultType,
  count: number
): ConfidenceScoreFieldResultType[] {
  const gateIds = new Set(result.hardGate?.gateFieldIds ?? []);
  const missing = result.fields.filter((field) => !field.earned);
  return [
    ...missing.filter((field) => gateIds.has(field.id)),
    ...missing.filter((field) => !gateIds.has(field.id))
  ].slice(0, count);
}
